import apiUrl from '../../../constants';
import axios from 'axios';
import CryptoJS from 'crypto-js';
import moment from 'moment';
import VueElementLoading from 'vue-element-loading';
import Paginate from 'vuejs-paginate';

const AUTH_HEADERS = { authorization: 'authkey xB84JJ89Hd25' };
const API_PAGE_SIZE = 25;
const UNMAPPED_FM_KEY = 'unmapped';

export default {
  name: 'CwbHoldRequestToDo',
  components: {
    VueElementLoading,
    Paginate,
  },
  data() {
    return {
      userEmail: '',
      isLoadingTodos: false,
      isSavingFmKey: null,
      todoRows: [],
      fmCheckState: {},
      allFmGroups: [],
      expandedFmKeys: {},
      fmPageIndex: 0,
      fmPageSize: 10,
    };
  },
  computed: {
    totalRows() {
      return this.allFmGroups.length;
    },
    fmPageCount() {
      return this.totalRows ? Math.ceil(this.totalRows / this.fmPageSize) : 0;
    },
    visibleFmGroups() {
      const start = this.fmPageIndex * this.fmPageSize;
      return this.allFmGroups.slice(start, start + this.fmPageSize);
    },
  },
  mounted() {
    const userdetailEncrypt = window.localStorage.getItem('userdetail');
    const bytes = CryptoJS.AES.decrypt(userdetailEncrypt.toString(), 'Key');
    const plaintext = bytes.toString(CryptoJS.enc.Utf8);
    const userdetail = JSON.parse(plaintext);
    this.userEmail = userdetail[0].email;
    this.loadHoldRequestTodos();
  },
  methods: {
    apiBase() {
      return apiUrl.api_url_hubops;
    },

    formatIst(gmtString) {
      if (!gmtString) return '--';
      const m = moment.utc(gmtString);
      return m.isValid() ? m.utcOffset(330).format('DD-MM-YYYY HH:mm:ss') : gmtString;
    },

    // HUB-8261: FM parent row owns checklist state for all child LMs.
    resetFmCheckState() {
      const next = {};
      this.allFmGroups.forEach((fm) => {
        const key = this.fmGroupKey(fm);
        const allFileUploaded = fm.lmTodos.every((row) => row.isFileUploaded);
        const allMarkedActive = fm.lmTodos.every((row) => row.isMarkedActive);
        next[key] = {
          isFileUploaded: allFileUploaded,
          isMarkedActive: allMarkedActive,
          fileUploadedLocked: allFileUploaded,
          markedActiveLocked: allMarkedActive,
        };
      });
      this.fmCheckState = next;
    },

    canCompleteFm(fm) {
      const state = this.fmCheckState[this.fmGroupKey(fm)];
      if (!state) return false;
      const fileNew = state.isFileUploaded && !state.fileUploadedLocked;
      const activeNew = state.isMarkedActive && !state.markedActiveLocked;
      return fileNew || activeNew;
    },

    buildFmTodoPayload(fm) {
      const ids = fm.lmTodos.map((row) => row.holdRequestStatusId);
      const state = this.fmCheckState[this.fmGroupKey(fm)];
      const fileAllTrue = !!(state.isFileUploaded && !state.fileUploadedLocked);
      const activeAllTrue = !!(state.isMarkedActive && !state.markedActiveLocked);

      return {
        holdRequestStatusIds: ids,
        isFileUploaded: ids.map(() => fileAllTrue),
        isMarkedActive: ids.map(() => activeAllTrue),
        updatedBy: this.userEmail,
      };
    },

    // HUB-8238: group flat LM todos under FM parent hubs for collapsible UI.
    buildFmTodoGroups(rows) {
      const map = {};
      rows.forEach((row) => {
        const fmId = row.fmParentHubId != null ? row.fmParentHubId : UNMAPPED_FM_KEY;
        if (!map[fmId]) {
          map[fmId] = {
            fmParentHubId: row.fmParentHubId,
            fmParentHubName: row.fmParentHubName || 'Unmapped FM',
            lmTodos: [],
          };
        }
        map[fmId].lmTodos.push(row);
      });

      return Object.values(map)
        .sort((a, b) => (a.fmParentHubName || '').localeCompare(b.fmParentHubName || ''))
        .map((group) => ({
          ...group,
          lmTodos: group.lmTodos.sort((a, b) =>
            (a.lmHubName || '').localeCompare(b.lmHubName || '')
          ),
          pendingCount: group.lmTodos.length,
        }));
    },

    fmGroupKey(group) {
      return String(group.fmParentHubId != null ? group.fmParentHubId : UNMAPPED_FM_KEY);
    },

    isFmExpanded(group) {
      return !!this.expandedFmKeys[this.fmGroupKey(group)];
    },

    toggleFmExpand(group) {
      const key = this.fmGroupKey(group);
      if (this.expandedFmKeys[key]) {
        this.$delete(this.expandedFmKeys, key);
        return;
      }
      this.$set(this.expandedFmKeys, key, true);
    },

    resetExpandedFmKeys() {
      this.expandedFmKeys = {};
    },

    async fetchAllHoldRequestTodos() {
      const allRows = [];
      let pageIndex = 0;
      let totalPages = 1;

      while (pageIndex < totalPages) {
        const res = await axios.get(`${this.apiBase()}listHoldRequestTodos`, {
          params: { pageIndex, pageSize: API_PAGE_SIZE },
          headers: AUTH_HEADERS,
        });

        if (!res.data || res.data.ResultCode !== 100) {
          return { ok: false, rows: [] };
        }

        const pageRows = res.data.ResponseData || [];
        allRows.push(...pageRows);

        const pg = res.data.pagination || {};
        totalPages = pg.totalPages != null ? pg.totalPages : 1;
        if (pageRows.length < API_PAGE_SIZE) {
          break;
        }
        pageIndex += 1;
      }

      return { ok: true, rows: allRows };
    },

    async loadHoldRequestTodos() {
      this.isLoadingTodos = true;
      try {
        const { ok, rows } = await this.fetchAllHoldRequestTodos();
        if (ok) {
          this.todoRows = rows;
          this.allFmGroups = this.buildFmTodoGroups(rows);
          this.resetFmCheckState();
          this.resetExpandedFmKeys();

          const maxPageIndex = this.fmPageCount ? this.fmPageCount - 1 : 0;
          if (this.fmPageIndex > maxPageIndex) {
            this.fmPageIndex = maxPageIndex;
          }
          return;
        }

        this.todoRows = [];
        this.allFmGroups = [];
        this.fmCheckState = {};
        this.fmPageIndex = 0;
        this.resetExpandedFmKeys();
      } catch (err) {
        console.error(err);
        this.todoRows = [];
        this.allFmGroups = [];
        this.fmCheckState = {};
        this.fmPageIndex = 0;
        this.resetExpandedFmKeys();
        this.$alertify.error('Unable to load pending todos');
      } finally {
        this.isLoadingTodos = false;
      }
    },

    getPaginationData(pageNum) {
      this.fmPageIndex = pageNum - 1;
      this.resetExpandedFmKeys();
    },

    async completeFmTodo(fm) {
      const fmKey = this.fmGroupKey(fm);
      if (!this.canCompleteFm(fm) || this.isSavingFmKey) {
        return;
      }
      this.isSavingFmKey = fmKey;
      try {
        const payload = this.buildFmTodoPayload(fm);
        const res = await axios.post(
          `${this.apiBase()}acknowledgeHoldRequestTodo`,
          payload,
          { headers: AUTH_HEADERS }
        );
        const body = res.data || {};
        if (body.ResultCode === 100) {
          this.$alertify.success(body.ReturnMessage);
          await this.loadHoldRequestTodos();
        } else {
          this.$alertify.error(body.ReturnMessage);
        }
      } catch (err) {
        console.error(err);
        this.$alertify.error('Unable to complete todo');
      } finally {
        this.isSavingFmKey = null;
      }
    },
  },
};



// WEBPACK FOOTER //
// ./src/views/components/CwbHoldRequestToDo/CwbHoldRequestToDo.js